import { ErrorHandler, Injectable } from '@angular/core'

/**
 * Catches otherwise-unhandled runtime errors. The real cause is logged for us
 * (console now; wire to a remote sink later) and never shown to the user — the
 * user only ever sees the friendly error pages/snackbars elsewhere in the app.
 *
 * One case we actively recover from: a failed lazy-chunk load. After a deploy,
 * the hashed bundle a long-open tab is still referencing no longer exists, so
 * the next navigation throws a ChunkLoadError. A one-time reload pulls the new
 * bundles and the navigation succeeds.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private static readonly RELOAD_FLAG = 'kontakta:chunk-reload'

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error)) {
      this.recoverFromStaleChunks()
      return
    }

    // Keep the full detail private to us — only visible in the console / logs.
    console.error('[Unhandled error]', error)
  }

  private isChunkLoadError(error: unknown): boolean {
    const message =
      (error as { message?: string })?.message ||
      (typeof error === 'string' ? error : '')
    return /ChunkLoadError|Loading chunk [\w-]+ failed|dynamically imported module/i.test(
      message
    )
  }

  private recoverFromStaleChunks(): void {
    // Guard against an infinite reload loop if the failure isn't deploy-related.
    if (sessionStorage.getItem(GlobalErrorHandler.RELOAD_FLAG)) {
      sessionStorage.removeItem(GlobalErrorHandler.RELOAD_FLAG)
      console.error('[Unhandled error] Chunk load failed after reload')
      return
    }

    sessionStorage.setItem(GlobalErrorHandler.RELOAD_FLAG, '1')
    window.location.reload()
  }
}
